import { useEffect, useMemo, useRef } from 'react';

import { DataProviderQueryStatus } from '../CustomDash/components/DataProvider/types';
import { formatProviderErrorMessage } from '../CustomDash/components/DataProvider/utils/data-provider-results-utils';
import { useTabContentConfig } from '../CustomDash/components/PageConfigProvider/PageConfigProvider';
import {
  useFilterContext,
  useIsTaxonomyResolved,
} from '../CustomDash/filters/contexts/FilterProvider';
import {
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  isMultiFilter,
} from '../CustomDash/filters/types/filters';
import { selectionNameLooksUnresolved } from '../CustomDash/filters/utils/patchUnresolvedSelectionNames';
import { useGaChartPreviewMeta } from './ga-chart-preview-meta-context';
import { getActiveNarrationCollector } from './ga-narration-collection-context';
import type {
  GlobalAiChartPreviewDataBranch,
  GlobalAiChartPreviewRuntimeSnapshot,
} from './global-ai-assistant-chart-preview-runtime';
import { useGlobalAIAssistantOptional } from './global-ai-assistant-context';
import { buildChartDataDigest } from './turn-outcome-digest';

/**
 * The RESOLVED display label(s) of the applied role filter, read from the
 * scope's labels-patched `activeFilterMap` (the same names the filter chips
 * render — the provider resolves them from the ACTIVE taxonomy's tree). The AI
 * response only carries taxonomy-relative numeric role ids, so this map is the
 * one place the FE knows the human name of the role the backend actually
 * resolved. Names that still look like raw ids (tree not yet loaded) are
 * skipped — the digest simply omits the label rather than reporting a number.
 */
export function resolvedRoleLabelFromFilterMap(
  filterMap: FilterSelectionsMap
): string | undefined {
  const roleFilter =
    filterMap.get(FilterId.MULTI_ROLE) ?? filterMap.get(FilterId.ROLE);
  if (!roleFilter) return undefined;
  const instances = isMultiFilter(roleFilter)
    ? roleFilter.instances
    : [roleFilter];
  const names = new Set<string>();
  for (const instance of instances) {
    if (instance.type !== FilterType.TREE) continue;
    for (const selection of instance.selections) {
      if (!selectionNameLooksUnresolved(selection.id, selection.name)) {
        names.add(selection.name);
      }
    }
  }
  if (names.size === 0) return undefined;
  return [...names].join(', ');
}

/**
 * Updates global-assistant chart preview runtime from data-provider state (GA preview only).
 */
export function GlobalAiChartPreviewDataReporter({
  itemId,
  branch,
  status,
  error,
  data,
  chartType,
  metric,
}: {
  itemId: string;
  branch: GlobalAiChartPreviewDataBranch;
  status: DataProviderQueryStatus;
  error: Error | { message?: string } | null | undefined;
  data: unknown;
  chartType: string;
  metric: unknown;
}) {
  const ga = useGlobalAIAssistantOptional();
  const mergeRuntimeRef = useRef<
    | ((
        id: string,
        patch: Partial<GlobalAiChartPreviewRuntimeSnapshot>
      ) => void)
    | undefined
  >(undefined);
  mergeRuntimeRef.current = ga?.mergeChartPreviewRuntime;
  const addTimelineEventRef = useRef<
    ((messageId: string, event: string) => void) | undefined
  >(undefined);
  addTimelineEventRef.current = ga?.addChartPreviewTimelineEvent;
  const meta = useGaChartPreviewMeta();
  const taxonomyResolved = useIsTaxonomyResolved();
  const { primaryFilter } = useTabContentConfig();
  // Resolved role label for the digest (see resolvedRoleLabelFromFilterMap).
  // `activeFilterMap` is the provider's labels-patched map, so this re-derives
  // (and the effect below re-reports) when the taxonomy tree lands and the
  // numeric role name self-heals into its display label.
  const { activeFilterMap } = useFilterContext();
  const resolvedRoleLabel = useMemo(
    () => resolvedRoleLabelFromFilterMap(activeFilterMap),
    [activeFilterMap]
  );
  const lastSnapRef = useRef('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    lastSnapRef.current = '';
  }, [meta?.messageId]);

  useEffect(() => {
    const merge = mergeRuntimeRef.current;
    if (!merge || !meta || meta.itemId !== itemId) {
      return;
    }
    const { messageId } = meta;
    const errStr = formatProviderErrorMessage(error) ?? '';
    const phase = (() => {
      if (status === DataProviderQueryStatus.Fetching) {
        return 'fetching' as const;
      }
      if (status === DataProviderQueryStatus.Error) {
        return 'error' as const;
      }
      if (status === DataProviderQueryStatus.Success) {
        if (chartType === 'text') {
          return undefined;
        }
        return 'success' as const;
      }
      return 'filters_ready' as const;
    })();
    const errSuffix = errStr.length > 0 ? `; err:${errStr.slice(0, 160)}` : '';
    const diag = `data:${branch}; status:${status}; chart:${chartType}; metric:${String(metric)}; taxonomy:${String(taxonomyResolved)}; hasData:${Boolean(data)}; primary:${primaryFilter?.id ?? 'none'}${errSuffix}`;
    // Include the resolved role label in the dedupe snapshot so a label that
    // self-heals AFTER data success (role tree lands late) still re-merges an
    // updated digest instead of being swallowed by the snapshot guard.
    const snap = `${messageId}|${phase ?? 'text_provider_success'}|${diag}|role:${resolvedRoleLabel ?? ''}`;
    if (lastSnapRef.current === snap) {
      return;
    }
    lastSnapRef.current = snap;
    // Providers can emit Success before the data payload lands (data still
    // null/undefined) — digesting that would narrate a healthy chart as
    // empty. Wait for an actual payload; a genuinely-empty result still
    // delivers an empty array, so honest-empty callouts are preserved.
    const dataDigest =
      status === DataProviderQueryStatus.Success && data != null
        ? {
            ...buildChartDataDigest({
              data,
              metric: String(metric),
              chartType,
            }),
            ...(resolvedRoleLabel ? { resolvedRoleLabel } : {}),
          }
        : undefined;
    merge(messageId, {
      ...(phase !== undefined ? { phase } : {}),
      ...(dataDigest ? { dataDigest } : {}),
      dataProviderBranch: branch,
      lastDataProviderStatus: String(status),
      providerDiagnostics: diag,
    });
    addTimelineEventRef.current?.(
      messageId,
      `provider ${branch} -> ${String(status)}`
    );
  }, [
    meta,
    itemId,
    branch,
    status,
    error,
    data,
    chartType,
    metric,
    taxonomyResolved,
    primaryFilter?.id,
    resolvedRoleLabel,
  ]);

  // Custom-page narration collection. Independent of the GA-preview effect
  // above: collection runs on REAL pages (the GA preview `meta` is absent
  // there). Reads the module-level registry — see ga-narration-collection-context
  // for why this can't be a React context and why re-checking on status/data
  // dep changes is sufficient. The collector self-dedupes, so reporting the
  // same item more than once is harmless.
  useEffect(() => {
    // Same premature-Success guard as the preview digest above: wait for an
    // actual data payload before digesting.
    if (status !== DataProviderQueryStatus.Success || data == null) return;
    const collector = getActiveNarrationCollector();
    if (!collector) return;
    collector.report(
      itemId,
      buildChartDataDigest({ data, metric: String(metric), chartType })
    );
  }, [status, itemId, data, metric, chartType]);

  return null;
}
